// Host adapter: keeps the tutorial's progress with the signed-in person.
//
// The tutorial module reads and writes progress synchronously through a ProgressStorage.
// This store answers from memory (seeded with the person's server record when the page
// opened), keeps a copy in this browser under the person's own key (so a dropped
// connection never loses the place), and saves to the server in the background. Steps
// read and actions practised only grow on the server; an explicit reset replaces them.
//
// A failed save never blocks learning: the state stays in memory and in this browser,
// and the next change (or a retry timer) sends it again.
import { progressStorageKey, type ProgressStorage } from '@/features/learning/progress'
import type { TutorialContent } from '@/features/learning'

export type SyncState = 'idle' | 'saving' | 'saved' | 'failed'
export type SaveMode = 'merge' | 'replace'

export type AccountStorageOptions = {
  content: TutorialContent
  personId: string
  /** The progress object the server returned for this person, or null. */
  serverProgress: unknown
  save: (progress: unknown, mode: SaveMode) => Promise<void>
  /** Refuse a delayed save after its originating identity changes; retain its local unsent copy. */
  canSave?: () => boolean
  onState?: (state: SyncState) => void
  /** Browser storage for the offline copy; null disables it. Default: window.localStorage. */
  local?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
  debounceMs?: number
  retryMs?: number
}

export function personProgressKey(content: TutorialContent, personId: string): string {
  return `${progressStorageKey(content)}:person:${personId}`
}

/** A reset leaves nothing started, read or practised: the server must replace, not merge. */
export function saveModeFor(progress: unknown): SaveMode {
  const p = progress as { hasStarted?: unknown; readStepIds?: unknown; practisedStepIds?: unknown } | null
  const empty = (v: unknown) => !Array.isArray(v) || v.length === 0
  return p && p.hasStarted !== true && empty(p.readStepIds) && empty(p.practisedStepIds) ? 'replace' : 'merge'
}

function browserLocal(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export function createAccountStorage(opts: AccountStorageOptions): ProgressStorage & { flush(): Promise<void> } {
  const key = progressStorageKey(opts.content)
  const localKey = personProgressKey(opts.content, opts.personId)
  const local = opts.local === undefined ? browserLocal() : opts.local
  const debounceMs = opts.debounceMs ?? 600
  const retryMs = opts.retryMs ?? 15_000

  const unsentKey = `${localKey}:unsent`
  const read = (k: string): string | null => {
    try {
      return local?.getItem(k) ?? null
    } catch {
      return null
    }
  }
  const localCopy = read(localKey)
  const unsent = read(unsentKey)
  // A change this person made here that never reached the server (the page was left
  // before the save) is newer than the server record: open at it and send it again.
  // Otherwise the server record wins, and this person's copy here covers a missing
  // record. Anonymous (signed-out) progress is never used: on a shared device it may be
  // someone else's.
  let cache: string | null = unsent ?? (opts.serverProgress != null ? JSON.stringify(opts.serverProgress) : localCopy)
  let pending: string | null = unsent ?? (opts.serverProgress == null && localCopy ? localCopy : null)
  let timer: ReturnType<typeof setTimeout> | null = null
  let inFlight: Promise<void> | null = null

  const state = (s: SyncState) => opts.onState?.(s)

  async function send(): Promise<void> {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    if (inFlight) await inFlight
    if (pending === null) return
    if (opts.canSave && !opts.canSave()) {
      state('failed')
      return
    }
    const body = pending
    pending = null
    state('saving')
    inFlight = (async () => {
      try {
        const parsed: unknown = JSON.parse(body)
        await opts.save(parsed, saveModeFor(parsed))
        if (pending === null) {
          try {
            local?.removeItem(unsentKey)
          } catch {
            // Harmless: the next open resends an already-saved state, which merges.
          }
        }
        state(pending === null ? 'saved' : 'saving')
      } catch {
        // Keep the newest unsent state; try again later.
        if (pending === null) pending = body
        state('failed')
        timer = setTimeout(() => void send(), retryMs)
      }
    })()
    await inFlight
    inFlight = null
  }

  function schedule() {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => void send(), debounceMs)
  }

  if (pending !== null) schedule()

  return {
    getItem(k: string) {
      return k === key ? cache : null
    },
    setItem(k: string, value: string) {
      if (k !== key) return
      cache = value
      try {
        local?.setItem(localKey, value)
        local?.setItem(unsentKey, value)
      } catch {
        // The server save still runs; nothing to do.
      }
      pending = value
      schedule()
    },
    flush: send,
  }
}
